import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  TextField,
  MenuItem,
  LinearProgress,
  IconButton,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { CreateIssueDialog } from '../components/CreateIssueDialog.js';

/** Maps the ticket's priority to a tracker priority label. */
const TRACKER_PRIORITY: Record<string, string> = {
  CRITICAL: 'Highest',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};
import {
  STATUS_COLOR,
  PRIORITY_COLOR,
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  cap,
  type TicketDetail,
  type TicketAssignee,
} from './ticketShared.js';
import { useT } from '../i18n/useT.js';

export function TicketDetailPage() {
  const all = useT();
  const copy = all.tickets.detail;
  const statuses = all.tickets.status;
  const priorities = all.tickets.priority;
  const common = all.common;
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const canTest = !!me?.organizations[0]?.tester;
  const base = `/v1/orgs/${orgId}/tickets/${id}`;
  const [note, setNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  const ticket = useQuery({
    enabled: !!orgId && !!id,
    queryKey: ['ticket', orgId, id],
    queryFn: async () => (await api.get(base)).data as TicketDetail,
  });
  const tk = ticket.data;

  // The linked session's AI summary, for the ticket's AI panel + the issue attach.
  const summary = useQuery({
    enabled: !!orgId && !!tk?.projectId && !!tk?.sessionId,
    queryKey: ['session-summary', tk?.sessionId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${tk!.projectId}/sessions/${tk!.sessionId}/summary`))
        .data as { summary?: string },
  });
  const aiSummary = summary.data?.summary;

  const members = useQuery({
    enabled: !!orgId,
    queryKey: ['members-lite', orgId],
    queryFn: async () => {
      try {
        return (await api.get(`/v1/orgs/${orgId}/members`)).data.data as {
          userId: string;
          fullName?: string;
          email: string;
        }[];
      } catch {
        return [] as { userId: string; fullName?: string; email: string }[];
      }
    },
  });

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.patch(base, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', orgId, id] });
      qc.invalidateQueries({ queryKey: ['tickets', orgId] });
      qc.invalidateQueries({ queryKey: ['ticket-counts', orgId] });
    },
  });

  const addNote = useMutation({
    mutationFn: async (body: string) => (await api.post(`${base}/notes`, { body })).data,
    onSuccess: () => {
      setNote('');
      qc.invalidateQueries({ queryKey: ['ticket', orgId, id] });
    },
  });

  const remove = useMutation({
    mutationFn: async () => (await api.delete(base)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets', orgId] });
      navigate('/tickets');
    },
  });

  if (ticket.isLoading) return <LinearProgress />;
  const t = ticket.data;
  if (!t) return <Typography>{copy.notFound}</Typography>;

  const sig = t.signals;
  const hasSignals =
    sig.rageClicks != null || sig.deadClicks != null || sig.jsErrors != null || sig.frustrationScore != null;

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag || t.tags.includes(tag)) return;
    patch.mutate({ tags: [...t.tags, tag] });
    setTagInput('');
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/tickets')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
          TK-{t.number}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          label={copy.priority}
          value={t.priority}
          onChange={(e) => patch.mutate({ priority: e.target.value })}
          sx={{ minWidth: 130 }}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              <Chip size="small" label={priorities[p] ?? cap(p)} sx={{ bgcolor: `${PRIORITY_COLOR[p]}22`, color: PRIORITY_COLOR[p], fontWeight: 700 }} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={copy.status}
          value={t.status}
          onChange={(e) => patch.mutate({ status: e.target.value })}
          sx={{ minWidth: 150 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              <Chip size="small" label={statuses[s] ?? cap(s)} sx={{ bgcolor: `${STATUS_COLOR[s]}22`, color: STATUS_COLOR[s], fontWeight: 700 }} />
            </MenuItem>
          ))}
        </TextField>
        {canTest && t.sessionId && t.projectId && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<BugReportIcon />}
            onClick={() => setIssueOpen(true)}
          >
            {copy.createBug}
          </Button>
        )}
        <IconButton color="error" onClick={() => setConfirmDelete(true)}>
          <DeleteOutlineIcon />
        </IconButton>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: 2 }}>
        {/* Main column */}
        <Stack spacing={2}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t.title}</Typography>
            {t.description ? (
              <Typography sx={{ whiteSpace: 'pre-wrap' }} color="text.secondary">{t.description}</Typography>
            ) : (
              <Typography color="text.disabled" fontStyle="italic">{copy.noDescription}</Typography>
            )}
            {t.screenshotUrl && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {copy.screenshot}
                </Typography>
                <Box
                  component="img"
                  src={t.screenshotUrl}
                  alt={copy.screenshotAlt}
                  sx={{ maxWidth: '100%', borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }}
                />
              </Box>
            )}
          </Paper>

          {hasSignals && (
            <Paper sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>{copy.signals}</Typography>
                {sig.replaySource && <Chip size="small" label={sig.replaySource} variant="outlined" />}
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                <Signal label={copy.duration} value={sig.durationMs != null ? `${Math.round(sig.durationMs / 1000)}s` : '—'} />
                <Signal label={copy.rageClicks} value={sig.rageClicks ?? 0} accent={sig.rageClicks ? '#ef4444' : undefined} />
                <Signal label={copy.deadClicks} value={sig.deadClicks ?? 0} accent={sig.deadClicks ? '#f59e0b' : undefined} />
                <Signal label={copy.jsErrors} value={sig.jsErrors ?? 0} accent={sig.jsErrors ? '#ef4444' : undefined} />
              </Box>
              {sig.frustrationScore != null && (
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{copy.frustrationScore}</Typography>
                    <Typography variant="caption" fontWeight={700}>{sig.frustrationScore}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={sig.frustrationScore}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': { bgcolor: frustrationColor(sig.frustrationScore) },
                    }}
                  />
                </Box>
              )}
              {t.projectId && t.sessionId && (
                <Button
                  component={RouterLink}
                  to={`/projects/${t.projectId}/sessions/${t.sessionId}`}
                  startIcon={<PlayArrowIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                >
                  {copy.watchReplay}
                </Button>
              )}
            </Paper>
          )}

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{copy.notes}</Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {t.notes.length === 0 && <Typography color="text.disabled" variant="body2">{copy.noNotes}</Typography>}
              {t.notes.map((n) => (
                <Stack key={n.id} direction="row" spacing={1.5}>
                  <Avatar sx={{ width: 30, height: 30, fontSize: 13 }}>{initials(n.author)}</Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="body2" fontWeight={600}>{n.author.name || n.author.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatDateTime(n.createdAt)}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{n.body}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder={copy.notePlaceholder}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && note.trim()) {
                    e.preventDefault();
                    addNote.mutate(note.trim());
                  }
                }}
              />
              <Button variant="contained" disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate(note.trim())}>
                {copy.addNote}
              </Button>
            </Stack>
          </Paper>
        </Stack>

        {/* Side column */}
        <Stack spacing={2}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{copy.details}</Typography>
            <Detail label={copy.customer} value={t.customerName || t.customerEmail || '—'} />
            {t.customerEmail && t.customerName && <Detail label={copy.email} value={t.customerEmail} />}
            {t.customerOrg && <Detail label={copy.company} value={t.customerOrg} />}
            <Box sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{copy.assignee}</Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={t.assignee?.id ?? ''}
                onChange={(e) => patch.mutate({ assigneeId: e.target.value || null })}
              >
                <MenuItem value="">{copy.unassigned}</MenuItem>
                {(members.data ?? []).map((m) => (
                  <MenuItem key={m.userId} value={m.userId}>{m.fullName || m.email}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Detail label={copy.created} value={formatDateTime(t.createdAt)} />
            <Detail label={copy.updated} value={formatDateTime(t.updatedAt)} />
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{copy.tags}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {t.tags.length === 0 && <Typography variant="body2" color="text.disabled">{copy.noTags}</Typography>}
              {t.tags.map((tag) => (
                <Chip
                  key={tag}
                  size="small"
                  label={tag}
                  onDelete={() => patch.mutate({ tags: t.tags.filter((x) => x !== tag) })}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder={copy.addTag}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
              <IconButton size="small" onClick={addTag}><AddIcon /></IconButton>
            </Stack>
          </Paper>

          {t.sessionId && (
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{copy.linkedSession}</Typography>
              <Link component={RouterLink} to={`/projects/${t.projectId}/sessions/${t.sessionId}`} variant="body2">
                {t.sessionId.slice(0, 8)}… →
              </Link>
            </Paper>
          )}
        </Stack>
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{copy.deleteTitle(t.number)}</DialogTitle>
        <DialogContent>
          <Typography>{copy.deleteBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>{common.cancel}</Button>
          <Button color="error" variant="contained" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {common.delete}
          </Button>
        </DialogActions>
      </Dialog>

      {orgId && t.projectId && t.sessionId && (
        <CreateIssueDialog
          open={issueOpen}
          onClose={() => setIssueOpen(false)}
          orgId={orgId}
          projectId={t.projectId}
          sessionId={t.sessionId}
          defaultTitle={t.title}
          description={t.description}
          tags={t.tags}
          defaultPriority={TRACKER_PRIORITY[t.priority]}
          assigneeName={t.assignee?.name || t.assignee?.email}
          aiSummary={aiSummary}
          jsErrors={t.signals.jsErrors}
        />
      )}
    </Box>
  );
}

function Signal({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ color: accent }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  );
}

function frustrationColor(score: number): string {
  if (score >= 70) return '#ef4444';
  if (score >= 40) return '#f59e0b';
  return '#2ec2b3';
}

function initials(u: TicketAssignee): string {
  const src = u.name || u.email;
  return src.slice(0, 2).toUpperCase();
}
